/*
 * Flash Sale - Countdown (BF26) und Ausblenden beim Ablauf. Gegenstueck zu snippets/flash-discount.liquid und
 * snippets/ll-flash-countdown.liquid.
 *
 * 1. <ll-flash-countdown>: Zeitfeld der BF26-Countdown-Leiste. Anzeige je nach Restzeit:
 *    mehr als 7 Tage -> "Endet am 30.11.", bis zur Umschaltschwelle -> "Noch 3 Tage", darunter -> "Endet in 10:13:25".
 *    Tage = Kalendertage in deutscher Zeit bis einschliesslich zum letzten gueltigen Tag (Ende 18.10. 00:00 ->
 *    letzter Tag 17.10.; am ganzen 15.10. also "Noch 3 Tage"), nicht abgerundete 24-Stunden-Bloecke.
 *    Datum und Uhrzeit immer in deutscher Zeit (Europe/Berlin), unabhaengig vom Besucher.
 * 2. [data-ll-flash-end]: Badge, Countdown oder Leiste wird ausgeblendet, sobald das Ende erreicht ist. Noetig, weil
 *    Shopify gerenderte Seiten cached und Liquid ein abgelaufenes Badge sonst noch eine Weile ausliefert.
 *
 * Ende: ISO-Zeitpunkt mit Offset (Feld end_datetime) oder JJJJ-MM-TT (end_date). JJJJ-MM-TT gilt wie im bisherigen
 * Countdown (assets/ll-custom.js.liquid) als Tagesbeginn in der Zeitzone des Besuchers.
 */
const DAY = 24 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_ZONE = "Europe/Berlin";
const MAX_TIMEOUT = 2147483647;

const parseEnd = (raw) => {
  const value = (raw || "").trim();
  const match = DATE_ONLY.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
  }
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : NaN;
};

const locale = document.documentElement.lang || "de";
const formatShortDate = new Intl.DateTimeFormat(locale, { timeZone: TIME_ZONE, day: "2-digit", month: "2-digit" });
const formatLongDate = new Intl.DateTimeFormat(locale, { timeZone: TIME_ZONE, day: "2-digit", month: "2-digit", year: "numeric" });
const formatTime = new Intl.DateTimeFormat(locale, { timeZone: TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const pad = (number) => String(number).padStart(2, "0");

/* Kalendertag in deutscher Zeit als fortlaufende Tageszahl (zum Zaehlen von Kalendertagen) */
const dayParts = new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" });
const calendarDay = (time) => {
  const parts = Object.fromEntries(dayParts.formatToParts(new Date(time)).map((part) => [part.type, part.value]));
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / DAY;
};

/* ---------------------------------------------------------------------------------------------------------------- */
/* Ausblenden beim Ablauf                                                                                           */
/* ---------------------------------------------------------------------------------------------------------------- */
let expiryTimer = null;

const checkExpiry = () => {
  const now = Date.now();
  let next = Infinity;

  document.querySelectorAll("[data-ll-flash-end]").forEach((element) => {
    const end = parseEnd(element.getAttribute("data-ll-flash-end"));
    if (!Number.isFinite(end)) {
      return;
    }
    if (end <= now) {
      element.hidden = true;
    } else {
      next = Math.min(next, end);
    }
  });

  clearTimeout(expiryTimer);
  if (Number.isFinite(next)) {
    expiryTimer = setTimeout(checkExpiry, Math.min(next - now + 50, MAX_TIMEOUT));
  }
};

let expiryQueued = false;
const queueExpiryCheck = () => {
  if (!expiryQueued) {
    expiryQueued = true;
    requestAnimationFrame(() => {
      expiryQueued = false;
      checkExpiry();
    });
  }
};

checkExpiry();
// Nachgeladene Inhalte (Infinite Scroll, Quick View, Section Rendering) ebenfalls pruefen.
new MutationObserver((mutations) => {
  if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1))) {
    queueExpiryCheck();
  }
}).observe(document.body, { childList: true, subtree: true });
document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && checkExpiry());
window.addEventListener("pageshow", checkExpiry);

/* ---------------------------------------------------------------------------------------------------------------- */
/* Countdown-Leiste BF26                                                                                             */
/* ---------------------------------------------------------------------------------------------------------------- */
class FlashCountdown extends HTMLElement {
  connectedCallback() {
    this.end = parseEnd(this.getAttribute("end"));
    const switchHours = Number.parseInt(this.getAttribute("switch-hours"), 10);
    this.switchMs = (Number.isFinite(switchHours) && switchHours > 0 ? switchHours : 48) * HOUR;
    this.value = this.querySelector(".ll-flash-bar__value");
    this.sentence = this.querySelector("[data-ll-flash-sentence]");

    if (!Number.isFinite(this.end) || !this.value) {
      return;
    }

    // Satz fuer Screenreader einmalig mit deutscher Zeit setzen (Liquid kennt bei JJJJ-MM-TT keine Uhrzeit).
    if (this.sentence) {
      const end = new Date(this.end);
      this.sentence.textContent = this.text("sentence")
        .replace("%date%", formatLongDate.format(end))
        .replace("%time%", formatTime.format(end));
    }

    // Timer laufen in Hintergrund-Tabs gedrosselt; beim Zurueckkommen sofort aktualisieren.
    this.abortController = new AbortController();
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && this.update(), { signal: this.abortController.signal });

    this.update();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.abortController?.abort();
  }

  text(name) {
    return this.getAttribute(`data-text-${name}`) || "";
  }

  update() {
    clearTimeout(this.timer);
    const now = Date.now();
    const remaining = this.end - now;

    if (remaining <= 0) {
      (this.closest("[data-ll-flash-end]") || this).hidden = true;
      return;
    }

    let label;
    let delay;

    // Letzter gueltiger Tag: Ende 18.10. 00:00 heisst, der 17.10. ist der letzte Tag.
    const lastDay = this.end - 1000;
    const daysLeft = calendarDay(lastDay) - calendarDay(now) + 1;

    if (daysLeft > 7) {
      label = this.text("date").replace("%date%", formatShortDate.format(new Date(lastDay)));
      delay = 60 * 1000;
    } else if (remaining > this.switchMs) {
      label = daysLeft === 1 ? this.text("day") : this.text("days").replace("%days%", daysLeft);
      delay = Math.min(60 * 1000, remaining - this.switchMs);
    } else {
      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const clock = `${pad(hours)}:${pad(Math.floor((totalSeconds % 3600) / 60))}:${pad(totalSeconds % 60)}`;
      label = this.text("clock").replace("%time%", clock);
      delay = (remaining % 1000) || 1000;
    }

    if (this.value.textContent !== label) {
      this.value.textContent = label;
    }
    this.timer = setTimeout(() => this.update(), Math.min(delay + 20, MAX_TIMEOUT));
  }
}

if (!window.customElements.get("ll-flash-countdown")) {
  window.customElements.define("ll-flash-countdown", FlashCountdown);
}
